import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { OriginalRootState } from 'redux/rootReducer'
import type { NewUserInfo, OnlineUserDetailResponse } from 'typings/onlineUser'
import { OnlineUserState } from 'typings/reduxStore'
import { ServerError } from 'typings/serverError'
// eslint-disable-next-line import/no-cycle
import { addOnlineUser, toggleUserMfa } from './async-thunks'

export const initState: OnlineUserState = {
  dataList: [],
  isDataLoading: true,
  isDetailDataLoading: true,
  error: false,
  errorMessage: '',
  isDeactivateSuccess: false,
  isSuccess: false,
  password: '',
  onlineUserDetail: {} as OnlineUserDetailResponse,
  errorCode: '',
  isUpdateSuccess: false,
  newUserId: ''
}

const onlineUserSlice = createSlice({
  name: 'onlineUserSlice',
  initialState: initState,
  reducers: {
    updateOnlineUserState: (
      state: OnlineUserState,
      action: PayloadAction<OnlineUserState>
    ) => {
      return {
        ...state,
        ...action.payload
      }
    }
  },
  extraReducers: builder => {
    /*
     * Since there is no loader to let the user know that the update is pending,
     * we are changing the toggle switch state as soon as the user clicks on it instead of waiting for the 204 response.
     *
     * This way the user will see that something happened and will not try to toggle the switch multiple times.
     */
    builder
      .addCase(toggleUserMfa.pending, (state: OnlineUserState) => {
        state.onlineUserDetail.userInfo.isOverrideEnabled =
          !state.onlineUserDetail?.userInfo?.isOverrideEnabled
      })
      .addCase(addOnlineUser.pending, (state: OnlineUserState) => {
        state.isUserAddedSuccess = false
        state.isUserExist = false
        state.errorCode = ''
      })
      .addCase(
        addOnlineUser.fulfilled,
        (state: OnlineUserState, action: PayloadAction<NewUserInfo>) => {
          const { userId, password, firstName } = action.payload
          const newUserInfo: NewUserInfo = {
            userId,
            password,
            firstName
          }
          state.error = false
          state.errorMessage = ''
          state.isUserAddedSuccess = true
          state.isUserExist = false
          state.onlineUserDetail = { ...state.onlineUserDetail, newUserInfo }
        }
      )
      .addCase(
        addOnlineUser.rejected,
        (state: OnlineUserState, action: PayloadAction<unknown>) => {
          const {
            response: { message },
            statusCode
          } = action.payload as ServerError

          state.error = true
          state.errorMessage = message
          state.isUserAddedSuccess = false
          state.isUserExist = false
          state.onlineUserDetail = {}

          if (statusCode === 422) {
            state.error = false
            state.isUserExist = true
          }
        }
      )
  }
})

export const { updateOnlineUserState } = onlineUserSlice.actions

export const selectOnlineUserEmail = (state: OriginalRootState) =>
  state.onlineUser.onlineUserDetail?.userInfo?.email

export const selectOnlineUserPhone = (state: OriginalRootState) =>
  state.onlineUser.onlineUserDetail?.userInfo?.phoneNumber

export const selectIsMfaEnabled = (state: OriginalRootState) =>
  state.onlineUser.onlineUserDetail?.userInfo?.isOverrideEnabled

export const selectDetailsExpandedUserId = (state: OriginalRootState) =>
  state.onlineUser.onlineUserDetail?.userInfo?.userId

export default onlineUserSlice.reducer
